import React, { ReactNode, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { getHomeBannerAds, requestAdTracking } from "../apis/ads";
import dayjs from "../libs/dayjs";
import {
  getHomeBannerTrackingUrl,
  HOME_BANNER_INTERVAL,
  HomeBannerAd,
  normalizeHomeBannerAds,
} from "../libs/ad-banner";
import { createVimpressionId } from "../libs/ad-popup";
import { tmapInterface } from "../libs/tmap-interface";
import {
  AD_INVENTORY_CODE,
  AD_TRACKING_EVENT_KEY,
  AdTrackingEventKey,
} from "../types/ads";
import "./HomeAdBanner.scss";

interface Props {
  euk: string;
  latitude: number;
  longitude: number;
  obscured: boolean;
  children: ReactNode;
}

const trackBanner = (ad: HomeBannerAd, key: AdTrackingEventKey, id: string) => {
  const url = getHomeBannerTrackingUrl(ad, key, id);
  if (url) {
    // Tracking must not block the native landing or the rest of the home screen.
    void requestAdTracking(url).catch(() => {
      console.warn("[EV] home banner tracking failed");
    });
  }
};

export function HomeAdBanner({
  euk,
  latitude,
  longitude,
  obscured,
  children,
}: Props) {
  const [ads, setAds] = useState<HomeBannerAd[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const container = useRef<HTMLDivElement>(null);
  const slider = useRef<Slider | null>(null);
  const impression = useRef<{ adId: string; id: string } | null>(null);
  const coordinates = useRef({ latitude, longitude });
  coordinates.current = { latitude, longitude };
  const ready =
    Boolean(euk) &&
    Number.isFinite(latitude) &&
    Math.abs(latitude) <= 90 &&
    latitude !== 0 &&
    Number.isFinite(longitude) &&
    Math.abs(longitude) <= 180 &&
    longitude !== 0;

  useEffect(() => {
    setAds([]);
    setCurrentIndex(0);
    setLoadedImages(new Set());
    if (!ready) return;

    let cancelled = false;
    const controller = new AbortController();
    const position = coordinates.current;
    getHomeBannerAds(
      {
        inventoryCode: AD_INVENTORY_CODE.HOME_ROLLING_BANNER,
        posTime: dayjs().format("YYYYMMDDHHmmss"),
        lat: String(position.latitude),
        lon: String(position.longitude),
      },
      controller.signal
    )
      .then((response) => {
        if (!cancelled) setAds(normalizeHomeBannerAds(response.ads));
      })
      .catch(() => {
        if (!cancelled) console.warn("[EV] home banner ad request failed");
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // Take one location snapshot per visit/account; map dragging must not reload ads.
  }, [ready, euk]);

  useEffect(() => {
    const updateVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", updateVisibility);
    return () =>
      document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    // Observe the image viewport, excluding the space reserved below the banner.
    const element =
      container.current?.querySelector<HTMLElement>(
        ".slick-list, .home-ad-banner__item"
      ) || container.current;
    if (!element) return;
    if (typeof IntersectionObserver !== "undefined") {
      const observer = new IntersectionObserver(([entry]) => {
        setVisible(
          entry.isIntersecting &&
            entry.intersectionRect.width >= 1 &&
            entry.intersectionRect.height >= 1
        );
      });
      observer.observe(element);
      return () => observer.disconnect();
    }

    const updateVisibility = () => {
      const rect = element.getBoundingClientRect();
      const width =
        Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
      const height =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      setVisible(width >= 1 && height >= 1);
    };
    updateVisibility();
    window.addEventListener("scroll", updateVisibility, true);
    window.addEventListener("resize", updateVisibility);
    return () => {
      window.removeEventListener("scroll", updateVisibility, true);
      window.removeEventListener("resize", updateVisibility);
    };
  }, [ads]);

  const activeAd = ads[currentIndex];
  const canShow = visible && pageVisible && !obscured;
  useEffect(() => {
    if (ads.length < 2) return;
    // Slick does not resume a paused slider when autoplay changes false -> true.
    if (canShow) slider.current?.slickPlay();
    else slider.current?.slickPause();
  }, [canShow, ads]);

  useEffect(() => {
    if (!canShow || !activeAd || !loadedImages.has(activeAd.adId)) {
      impression.current = null;
      return;
    }
    // Slick's cloned slides can load the same image more than once.
    if (impression.current?.adId === activeAd.adId) return;
    const id = createVimpressionId();
    impression.current = { adId: activeAd.adId, id };
    trackBanner(activeAd, AD_TRACKING_EVENT_KEY.VIEWABLE_IMPRESSION, id);
  }, [activeAd, canShow, loadedImages]);

  const openBanner = (ad: HomeBannerAd) => {
    if (!ad.landingUrl || !canShow || !loadedImages.has(ad.adId)) return;
    const id =
      impression.current?.adId === ad.adId
        ? impression.current.id
        : createVimpressionId();
    trackBanner(ad, AD_TRACKING_EVENT_KEY.CLICK, id);
    tmapInterface.openBrowser(ad.landingUrl);
  };

  const renderAd = (ad: HomeBannerAd, index: number) => {
    const image = (
      <img
        src={ad.imageUrl}
        alt={`EV충전 배너 ${index + 1}`}
        width={960}
        height={189}
        draggable={false}
        onLoad={() =>
          setLoadedImages((current) =>
            current.has(ad.adId) ? current : new Set(current).add(ad.adId)
          )
        }
        onError={() => {
          setCurrentIndex(0);
          setAds((current) => current.filter((item) => item.adId !== ad.adId));
        }}
      />
    );

    return (
      <div key={ad.adId}>
        {ad.landingUrl ? (
          <button
            type="button"
            className="home-ad-banner__item"
            aria-label={`EV충전 배너 ${index + 1} 자세히 보기`}
            tabIndex={index === currentIndex ? 0 : -1}
            onClick={() => openBanner(ad)}
          >
            {image}
          </button>
        ) : (
          <div className="home-ad-banner__item">{image}</div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={container}
      className={`home-ad-banner${ads.length ? " home-ad-banner--active" : ""}`}
    >
      {ads.length > 1 ? (
        <Slider
          ref={slider}
          key={ads.map((ad) => ad.adId).join(",")}
          infinite
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
          autoplay
          autoplaySpeed={HOME_BANNER_INTERVAL}
          arrows={false}
          dots={false}
          className="banner_slider_custom"
          afterChange={setCurrentIndex}
        >
          {ads.map(renderAd)}
        </Slider>
      ) : ads.length === 1 ? (
        renderAd(ads[0], 0)
      ) : (
        children
      )}
    </div>
  );
}
